import React, { memo, useEffect, useState } from "react";
import Modal from "react-modal";
import classNames from "classnames";
import { Button } from "components/Shared";
import { LocalNotification } from "lib";
import { SlaTaskListItem, SlaTaskDetail, SlaStatus, SlaAssignee } from "../types";
import { STATUS_COLORS, slaStatusLabel } from "../slaStatusDisplay";
import axios from "v2/utils/axios";

interface Props {
  isOpen: boolean;
  task: SlaTaskListItem | null;
  onClose: () => void;
}

const SlaTaskDetailModal = ({ isOpen, task, onClose }: Props) => {
  const [detail, setDetail] = useState<SlaTaskDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingReminder, setIsSendingReminder] = useState(false);

  const taskId = task?.id;
  useEffect(() => {
    if (!isOpen || !taskId) return;
    setDetail(null);
    setIsLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_ENDPOINT_V2}sla_dashboard/tasks/${taskId}`)
      .then((res) => {
        const attrs = res.data?.data?.attributes ?? res.data;
        const id = res.data?.data?.id ?? taskId;
        setDetail({ id, ...attrs } as SlaTaskDetail);
      })
      .catch((err) => {
        console.error("Error fetching SLA task detail:", err);
      })
      .finally(() => setIsLoading(false));
  }, [isOpen, taskId]);

  const handleSendReminder = async () => {
    if (!task) return;
    setIsSendingReminder(true);
    try {
      const res = await axios.post<{
        data: {
          status: "queued";
          task_id: string;
          queued_at: string;
          sla_status?: "on_time" | "at_risk" | "breached";
        };
      }>(
        `${process.env.REACT_APP_API_ENDPOINT_V2}sla_dashboard/tasks/${task.id}/send_reminder`
      );

      const slaStatus = res.data?.data?.sla_status;
      const successMessage =
        slaStatus === "at_risk"
          ? "At-risk SLA reminder sent"
          : slaStatus === "breached"
          ? "Breached SLA reminder sent"
          : "Reminder sent"; // fallback if the field is absent (older API)

      new LocalNotification(successMessage, "tcbot", "success");
    } catch (err) {
      const error = err as any;
      const status = error?.response?.status;
      const detail: string | undefined = error?.response?.data?.errors?.detail?.[0];

      if (status === 422 && detail && detail.toLowerCase().includes("on time")) {
        // contract §3.6 — on_time tasks don't need reminders; informational, not error tone
        new LocalNotification(detail, "tcbot", "info");
      } else if (status === 422 && detail) {
        // contract §3.1 (no assignees) and §3.3/§3.4 (no_sla / closed)
        // Pass the server's exact wording through to the user; backend owns the copy.
        new LocalNotification(detail, "Error", "error");
      } else {
        console.error("Error sending reminder:", err);
        new LocalNotification("Something went wrong", "Error", "error");
      }
    } finally {
      setIsSendingReminder(false);
    }
  };

  const d = detail ?? (task as any);
  const statusColor =
    d?.sla_status && STATUS_COLORS[d.sla_status as SlaStatus]
      ? STATUS_COLORS[d.sla_status as SlaStatus]
      : STATUS_COLORS["no_sla"];

  const daysLabel =
    d?.days_remaining != null
      ? d.days_remaining >= 0
        ? `Due in ${d.days_remaining} day${d.days_remaining !== 1 ? "s" : ""}`
        : `${Math.abs(d.days_remaining)} day${Math.abs(d.days_remaining) !== 1 ? "s" : ""} overdue`
      : "";

  // Shift a "YYYY-MM-DD" date string by a whole number of calendar days,
  // computed in UTC to avoid local-timezone drift.
  const addDays = (dateStr: string, offset: number): string => {
    const [y, m, day] = dateStr.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, day));
    dt.setUTCDate(dt.getUTCDate() + offset);
    const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(dt.getUTCDate()).padStart(2, "0");
    return `${dt.getUTCFullYear()}-${mm}-${dd}`;
  };
  // Format an ISO "YYYY-MM-DD" date string for display as "DD-MM-YYYY".
  // Date arithmetic (addDays) stays in ISO internally; only the display is reformatted.
  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return "";
    const [y, m, day] = dateStr.split("-");
    if (!y || !m || !day) return dateStr;
    return `${day}-${m}-${y}`;
  };
  const rangeLabel = (from: string, to: string) => `${formatDate(from)} – ${formatDate(to)}`;

  const due: string | undefined = d?.due_at ?? undefined;
  // A threshold of 0 (or nil) means that bucket is not configured — the band is
  // shown as "—". Coerce each threshold to a positive integer or 0.
  // Two-threshold model: `t` = upcoming (Target) days, `b` = breached days.
  // At risk is DERIVED (the gap between the due date and the breach point) — there
  // is no at_risk input anymore.
  const effectiveTargetRaw = d?.sla_effective_target_days ?? d?.sla_target_days;
  const t = Number(effectiveTargetRaw) > 0 ? Number(effectiveTargetRaw) : 0;
  const b = Number(d?.sla_breached_days) > 0 ? Number(d?.sla_breached_days) : 0;
  const slaConfigured = t > 0 || b > 0;
  // The due date itself is still within the "upcoming"/on-time window, so a breach
  // can start no earlier than the day after the due date. breached = 0 therefore
  // means "breaches as soon as it's overdue" (due + 1), with no at-risk grace.
  const breachOffset = Math.max(b, 1);

  // Upcoming window: the `target`-day band ending on and INCLUDING the due date ⇒
  // [due - (target - 1), due]. Only shown when target > 0.
  const upcomingDates =
    due && t > 0 ? rangeLabel(addDays(due, -(t - 1)), due) : "—";
  // At risk window (DERIVED): overdue but not yet breached ⇒ [due + 1, due + breached - 1].
  // Only meaningful when breached > 1 (breached 0 or 1 leaves no at-risk days).
  const atRiskDates =
    due && b > 1 ? rangeLabel(addDays(due, 1), addDays(due, b - 1)) : "—";
  // Breached: on or after the breach point ⇒ due + breachOffset (open-ended).
  // For breached = 0 this is due + 1 (breaches immediately when overdue).
  // Shown for any SLA-configured task.
  const breachedDates =
    due && slaConfigured ? `${formatDate(addDays(due, breachOffset))} onwards` : "—";

  const taskDueDateLabel = due ? (daysLabel ? `${formatDate(due)} (${daysLabel})` : formatDate(due)) : "—";

  return (
    <Modal
      ariaHideApp={false}
      isOpen={isOpen}
      onRequestClose={onClose}
      className="task-edit-modal"
      contentLabel="SLA Task Detail"
    >
      <header>
        <div className="modal-header-body">
          <h4 className="font-bold text-18">Task SLA Details</h4>
        </div>
        <button type="button" className="close" aria-label="Close" onClick={onClose}>
          <span aria-hidden="true">×</span>
        </button>
      </header>

      <section>
        {isLoading && <div className="py-8 text-center text-black-60">Loading…</div>}

        {!isLoading && d && (
          <>
            {/* Status badge */}
            <div className="mb-4">
              <span
                className={classNames(
                  "inline-block px-3 py-1 text-13 font-semibold rounded-full",
                  statusColor
                )}
              >
                {slaStatusLabel(d.sla_status)}
              </span>
            </div>

            {/* Two-column metadata grid */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 mb-4 text-13">
              <div>
                <div className="text-11 text-black-40 uppercase mb-1">Task Name</div>
                <div className="font-semibold">{d.name}</div>
              </div>
              <div>
                <div className="text-11 text-black-40 uppercase mb-1">Project</div>
                <div>{d.project_full_name ?? d.project_name}</div>
              </div>
              <div>
                <div className="text-11 text-black-40 uppercase mb-1">Phase</div>
                <div>{d.phase ?? "—"}</div>
              </div>
              <div>
                <div className="text-11 text-black-40 uppercase mb-1">Assigned To</div>
                <div>
                  {d.assignees && d.assignees.length > 0
                    ? d.assignees.map((a: SlaAssignee) => `${a.firstname} ${a.surname}`.trim()).join(", ")
                    : "—"}
                </div>
              </div>
              <div>
                <div className="text-11 text-black-40 uppercase mb-1">Task Due Date</div>
                <div>{taskDueDateLabel}</div>
              </div>
              <div>
                <div className="text-11 text-black-40 uppercase mb-1">Priority</div>
                <div className="capitalize">{d.priority ?? "—"}</div>
              </div>
            </div>

            {/* Grouped SLA date windows — shown for every SLA task regardless of
                its current status. Boxed and left-aligned, with the "SLA Dates"
                header straddling the top border on the right (legend-style). */}
            <div
              className="relative mt-3 mb-4 border border-black-20 rounded p-3"
              style={{ maxWidth: 320 }}
            >
              <div
                className="absolute text-11 text-black-40 uppercase font-semibold bg-white px-1"
                style={{ top: -8, left: 8 }}
              >
                SLA Dates
              </div>
              <div className="grid grid-cols-1 gap-y-2 text-13">
                <div>
                  <div className="text-11 text-black-40 uppercase mb-1">Upcoming Dates</div>
                  <div>{upcomingDates}</div>
                </div>
                <div>
                  <div className="text-11 text-black-40 uppercase mb-1">At Risk Dates</div>
                  <div>{atRiskDates}</div>
                </div>
                <div>
                  <div className="text-11 text-black-40 uppercase mb-1">Breached Dates</div>
                  <div>{breachedDates}</div>
                </div>
              </div>
            </div>

            {/* Description */}
            {d.description && (
              <div className="mb-4">
                <div className="text-11 text-black-40 uppercase mb-1">Description</div>
                <p className="text-13 text-black-80 whitespace-pre-line">{d.description}</p>
              </div>
            )}

          </>
        )}
      </section>

      <footer>
        <Button
          onClick={() => {
            // Open the full task in a new tab; keep this modal open so the user
            // can return to it after viewing.
            if (task) window.open(`/tasks/${task.id}`, "_blank", "noopener,noreferrer");
          }}
        >
          View Full Task
        </Button>
        {(() => {
          const isClosed =
            d?.lifecycle_status === "completed" || d?.sla_status === "closed";
          if (isClosed) return null;
          const isOnTime = task?.sla_status === "on_time";
          const btnDisabled = isSendingReminder || isOnTime;
          const btnTitle = isOnTime ? "On-time tasks don't need a reminder." : "";
          return (
            <span
              title={btnTitle}
              style={{ display: "inline-flex", ...(isOnTime ? { cursor: "not-allowed" } : {}) }}
            >
              <Button
                kind="primary"
                onClick={handleSendReminder}
                disabled={btnDisabled}
              >
                {isSendingReminder ? "Sending…" : "Send Reminder"}
              </Button>
            </span>
          );
        })()}
      </footer>
    </Modal>
  );
};

export default memo(SlaTaskDetailModal);
